import { useTranslation } from 'react-i18next'

import type { ColorScheme } from '@hivewatch/dark'

import { FilterDrawer } from '@/components/filter-drawer'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { removeNullValues } from '@/redux/incidents/utils'
import {
  selectIncidentRulesActionsFilter,
  selectIncidentRulesFacilityIdsFilter,
  selectIncidentRulesStatusFilter,
  setIncidentRulesActionsFilter,
  setIncidentRulesFacilityIdsFilter,
  setIncidentRulesStatusFilter,
} from '@/redux/ui/uiSlice'
import { BUTTON_PRESS, trackEvent } from '@/utils/analytics'

import type { RulesFilterFormInputsI } from '../types/form'
import { DarkRulesFilterForm } from './DarkRulesFilterForm'

const FILTER_FIELD_NAMES: Array<keyof RulesFilterFormInputsI> = [
  'facilityIds',
  'actions',
  'status',
]

interface DarkRulesFilterDrawerIProps {
  isOpen: boolean
  onClose: () => void
  colorScheme: ColorScheme
}

export const DarkRulesFilterDrawer = ({
  isOpen,
  onClose,
  colorScheme,
}: DarkRulesFilterDrawerIProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const facilityIdsFilterValue = useAppSelector(
    selectIncidentRulesFacilityIdsFilter
  )
  const actionsFilterValue = useAppSelector(selectIncidentRulesActionsFilter)
  const statusFilterValue = useAppSelector(selectIncidentRulesStatusFilter)

  const defaultValues: RulesFilterFormInputsI = {
    facilityIds: facilityIdsFilterValue ?? null,
    actions: actionsFilterValue ?? null,
    status: statusFilterValue ?? null,
  }

  const onApply = (values: RulesFilterFormInputsI) => {
    dispatch(setIncidentRulesFacilityIdsFilter(values.facilityIds ?? null))
    dispatch(setIncidentRulesActionsFilter(values.actions ?? null))
    dispatch(setIncidentRulesStatusFilter(values.status ?? null))

    trackEvent('Incident Rules View: Filters Applied', {
      filters: removeNullValues({
        facilityIds: values.facilityIds,
        actions: values.actions,
        status: values.status,
      }),
    })
  }

  return (
    <FilterDrawer<RulesFilterFormInputsI>
      colorScheme={colorScheme}
      defaultValues={defaultValues}
      fieldNames={FILTER_FIELD_NAMES}
      isOpen={isOpen}
      onApply={onApply}
      onClear={() => trackEvent(`${BUTTON_PRESS} Rules Drawer - Clear Filters`)}
      onClose={onClose}
      title={t('automations:filter_rules_title')}
    >
      {(methods) => (
        <DarkRulesFilterForm
          colorScheme={colorScheme}
          control={methods.control}
        />
      )}
    </FilterDrawer>
  )
}
